// React
import { useContext, useEffect } from "react"

// Components
import { BaseTable, DateInput, HeaderMenu, Input, Label, Loading, NumberInput, Select, TableHeader, TableSection } from "components"
import { Code } from "./Code"

// Contexts
import { DataContext } from "../contexts"

// Form
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"

// Icons
import { TbCheck, TbX } from "react-icons/tb"

// Third-Party Libraries
import moment from "moment"

// Types
import type { DataType } from "../types"

// Utils
import { convertNumber, useEmployee, useHandleAuth, useLocation, useDataCoaByCode } from "utils"
import { type FormType, checkQtyStatus } from "../utils"

export function InputSection(params: {
  ingredientsLoading?: boolean
}): JSX.Element {
  // Hooks
  const { ingredient, product } = useContext(DataContext)
  const { dataCoa } = useDataCoaByCode("LA13")

  // Form
  const { control, setValue } = useFormContext<FormType>()
  const [qty_build_unit] = useWatch({
    control,
    name: ["qty_build_unit"]
  })
  const [total_cac, total_amount, overhead] = useWatch({
    control,
    name: ["total_cac", "total_amount", "overhead"]
  })

  useEffect(() => {
    if (qty_build_unit !== undefined) {
      const total_cac = qty_build_unit * product.component_at_cost
  
      const total_amount = ingredient.reduce((sum, item) => {
        const qty = item.qty * qty_build_unit
        const amount = qty * item.avg_price
        return sum + amount
      }, 0)
  
      const overhead = total_cac - total_amount 
  
      setValue("total_cac", Math.round(total_cac))
      setValue("total_amount", Math.round(total_amount))
      setValue("overhead", Math.round(overhead))
    }
    // eslint-disable-next-line
  }, [qty_build_unit, setValue])

  useEffect(() => {
    if (dataCoa !== undefined) {

      const coa_id = dataCoa?.coa_id ?? 0
  
      setValue("coa_overhead_id", coa_id)
    }
    // eslint-disable-next-line
  }, [dataCoa, setValue])


  return (
    <>
      <HeaderMenu title="DATA ENTRY | BUILD ITEM">
        <Code />
      </HeaderMenu>

      <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
        <section className="flex flex-col gap-3">
          <Input
            disabled
            label="CODE"
            value={product.product_barcode}
          />

          <Input
            disabled
            label="DESCRIPTION"
            value={product.product_name}
          />

          <Input
            disabled
            label="UNIT"
            value={product.mr_unit_name}
          />
          
          <Controller
            control={control}
            name="component_at_cost"
            render={({ field, fieldState }) => (
              <NumberInput
                label="COMPONENT AT COST"
                disabled={true}
                error={fieldState.error?.message}
                value={Number(product.component_at_cost??0).toFixed(2)}
                ref={field.ref}
              />
            )}
          />
          <Input
            disabled
            label="ACCOUNT OVERHEAD PRODUCTION"
            value={`${dataCoa?.coa_code} ${dataCoa?.coa_name}`}
          />
        </section>

        <section className="flex flex-col gap-3">
          <Controller
            control={control}
            name="transaction_date"
            render={({ field, fieldState }) => (
              <DateInput
                label="DATE"
                disabled={field.disabled}
                error={fieldState.error?.message}
                selected={moment(field.value).toDate()}
                ref={field.ref}
                onChange={(res) => field.onChange(moment(res).format("YYYY-MM-DD"))}
              />
            )}
          />

          <Location />

          <Controller
            control={control}
            name="qty_build_unit"
            render={({ field, fieldState }) => (
              <NumberInput
                label="QTY BUILD"
                disabled={field.disabled}
                error={fieldState.error?.message}
                value={field.value}
                ref={field.ref}
                onValueChange={(res) => field.onChange(res.floatValue)}
              />
            )}
          />

          <Employee />
        </section>
      </section>

      <Ingredient data={ingredient} isLoading={params.ingredientsLoading} />

      <section className="grid grid-cols-2">
        <section className="flex flex-col">
          <TableSection>
            <TableSection.Row
              label="STATUS APPROVE"
              value={product.approve_status}
              className="text-uppercase"
            />

            <TableSection.Row
              label="APPROVED BY"
              value={product.approve_by_name??"-"}
              className="text-uppercase"
            />
          </TableSection>
        </section>
        <section className="flex flex-col items-end">
          <BaseTable
            plain
            className="w-full min-w-[100px] md:max-w-[400px] text-right"
          >
            <tbody>
              <tr className="font-bold">
                <td className="whitespace-nowrap">TOTAL CAC</td>
                <td className="w-[10px]">:</td>
                <td className="text-lg">{convertNumber(total_cac).intoCurrency}</td>
              </tr>
              <tr className="font-bold">
                <td className="whitespace-nowrap">TOTAL AMOUNT</td>
                <td className="w-[10px]">:</td>
                <td className="text-lg">{convertNumber(total_amount).intoCurrency}</td>
              </tr>
              <ErrorMessage
                  errors={control._formState.errors}
                  name="total_amount"
                  render={({ message }) => (
                    <tr className="!m-0 !p-0 !border-none">
                      <td colSpan={3} className="text-sm text-red-500">{message}</td>
                    </tr>
              )}/>
              <tr className="font-bold">
                <td className="whitespace-nowrap">OVERHEAD</td>
                <td className="w-[10px]">:</td>
                <td className="text-lg">{convertNumber(overhead).intoCurrency}</td>
              </tr>
            </tbody>
          </BaseTable>
        </section>
      </section>
    </>
  )
}

function Employee(): JSX.Element {
  // Hooks
  const { dataEmployee, isLoadingEmployee } = useEmployee()
  const { userTokenData } = useHandleAuth()

  // Vars
  const data = userTokenData()

  // Form
  const { control, formState, setValue } = useFormContext<FormType>()

  useEffect(() => {
    if (data && dataEmployee.length && !formState.defaultValues?.employee_id) {
      // Vars
      const result = dataEmployee.find(item => item.value === data.user_id.toString())

      if (result) {
        setValue("employee_id", result?.value ?? "")
      }
    }

    // eslint-disable-next-line
  }, [data, dataEmployee, formState])

  return (
    <Controller
      name="employee_id"
      control={control}
      render={({ field, fieldState }) => (
        <Select
          label="EMPLOYEE"
          placeholder="Select Employee"
          error={fieldState.error?.message}
          isDisabled={field.disabled}
          isLoading={isLoadingEmployee}
          options={dataEmployee}
          value={dataEmployee.find((item) => item.value === field.value) ?? null}
          ref={field.ref}
          onChange={(res) => {
            if (res) {
              field.onChange(res.value)
            }
          }}
        />
      )}
    />
  )
}

function Ingredient(params: {
  data: DataType["ingredient"]
  isLoading?: boolean
}): JSX.Element {
  // Vars
  const ingredient = params.data

  // Form
  const { control } = useFormContext<FormType>()
  const qty_build_unit = useWatch({
    control,
    name: "qty_build_unit"
  })

  return (
    <section>
      <Label>INGREDIENT</Label>

      <BaseTable>
        <TableHeader>
          <tr>
            <th>NO</th>
            <th>CODE</th>
            <th>DESCRIPTION</th>
            <th>UNIT</th>
            <th>SOH</th>
            <th>QTY</th>
            <th>BUILD</th>
            <th>AVERAGE</th>
            <th>AMOUNT</th>
            
            <th>#</th>
          </tr>
        </TableHeader>

        <tbody>
          {params.isLoading ? (
            <tr>
              <td colSpan={10}>
                <Loading loading />
              </td>
            </tr>
          ) : !ingredient.length ? (
            <tr>
              <td colSpan={10}>
                <Loading errorText="No data available" />
              </td>
            </tr>
          ) : ingredient.map((item, key) => {
            // Vars
            const qty: number = item.qty * qty_build_unit
            const amount: number = qty * Number(item.avg_price)

            return (
              <tr key={key}>
                <td className="text-center">{key + 1}</td>
                <td className="text-center">{item.product_barcode}</td>
                <td>{item.product_name}</td>
                <td>{item.mr_unit_name}</td>
                <td className="text-right">{convertNumber(item.soh).intoCurrency}</td>
                <td className="text-right">{convertNumber(item.qty).intoCurrency}</td>
                <td className="text-right">{convertNumber(qty).intoCurrency}</td>
                <td className="text-right">{convertNumber(Number(item.avg_price)).intoCurrency}</td>
                <td className="text-right">{convertNumber(amount).intoCurrency}</td>
                <td>
                  <section className="flex justify-center">
                    {checkQtyStatus(
                      item.qty,
                      qty_build_unit,
                      item.soh,
                      Number(item.avg_price),
                    ) ? (
                      <TbCheck color="green" />
                    ) : (
                      <TbX color="red" />
                    )}
                  </section>
                </td>
              </tr>
            )
          })}
        </tbody>
      </BaseTable>
    </section>
  )
}

function Location(): JSX.Element {
  // Hooks
  const { data, isLoading } = useLocation()

  // Form
  const { control } = useFormContext<FormType>()

  return (
    <Controller
      name="location_id"
      control={control}
      render={({ field, fieldState }) => (
        <Select
          label="LOCATION"
          placeholder="Select Location"
          error={fieldState.error?.message}
          isDisabled={field.disabled}
          isLoading={isLoading}
          options={data}
          value={data.find((item) => item.value === field.value) ?? null}
          ref={field.ref}
          onChange={(res) => {
            if (res) {
              field.onChange(res.value)
            }
          }}
        />
      )}
    />
  )
}